class QuickCheckoutComponent extends HTMLElement {
  #abortController = new AbortController();

  /** How long to wait for the free-gift app to add its gift before going to checkout. */
  #giftWaitMs = 3000;
  #giftPollMs = 300;
  #giftProductType = 'giftbox_ghost_product';

  connectedCallback() {
    const { signal } = this.#abortController;
    const scope = this.closest('.shopify-section, dialog, product-card');

    scope?.addEventListener('variant:update', this.#onVariantUpdate, { signal });
    this.querySelector('form')?.addEventListener('submit', this.#onBeforeSubmit, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /**
   * Keep the variant ID in sync and reflect availability on the button.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    const variantInput = this.querySelector('input[name="id"]');
    const button = this.querySelector('button[type="submit"]');

    if (!variantInput || !button) return;

    variantInput.value = variant?.id ?? '';
    button.disabled = !(variant?.available ?? false);
  };

  /**
   * Before the native form submits, sync the variant ID and selling_plan from
   * the main product form — the authoritative source for whatever the user has
   * currently selected, including any subscription selling plan.
   *
   * Subscription purchases: instead of the native POST (which jumps straight to
   * checkout and skips the free-gift app), add the item via the Cart API, give
   * the gift app a moment to add the free gift, then go to checkout.
   * One-time purchases keep the original native behaviour.
   */
  #onBeforeSubmit = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const scope = this.closest('.shopify-section, dialog, product-card');
    const mainForm = scope?.querySelector('form[data-type="add-to-cart-form"]');

    if (mainForm) {
      // Always read the freshest variant ID directly from the main form.
      const mainVariantId = mainForm.querySelector('[ref="variantId"]')?.value;
      const variantInput = this.querySelector('input[name="id"]');
      if (variantInput && mainVariantId) {
        variantInput.value = mainVariantId;
      }

      // Carry any selling_plan (native subscriptions) through to checkout.
      const sellingPlanValue = mainForm.querySelector('input[name="selling_plan"]')?.value ?? '';
      const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput) {
        sellingPlanInput.value = sellingPlanValue;
        sellingPlanInput.disabled = !sellingPlanValue;
      }
    }

    const sellingPlan = this.querySelector('input[name="selling_plan"]')?.value ?? '';
    if (!sellingPlan) return; // one-time purchase: let the native form submit as before

    event.preventDefault();

    const button = this.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const root = window.Shopify?.routes?.root || '/';
    const id = Number(this.querySelector('input[name="id"]')?.value);
    const quantity = Number(this.querySelector('input[name="quantity"]')?.value || 1);

    let added = false;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity, selling_plan: Number(sellingPlan) }] }),
      });
      added = response.ok;
    } catch (error) {
      console.error(error);
    }

    if (!added) {
      // Cart API failed: fall back to the original native submit (no gift wait).
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      form.submit();
      return;
    }

    await this.#waitForGift(root);
    window.location.href = `${root}checkout`;
  };

  /**
   * Poll the cart until the free-gift line appears, or until the timeout.
   * @param {string} root
   */
  async #waitForGift(root) {
    const deadline = Date.now() + this.#giftWaitMs;

    while (Date.now() < deadline) {
      try {
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        if (cart?.items?.some((item) => item.product_type === this.#giftProductType)) return true;
      } catch (error) {
        console.error(error);
      }
      await new Promise((resolve) => setTimeout(resolve, this.#giftPollMs));
    }

    return false;
  }
}

if (!customElements.get('quick-checkout-component')) {
  customElements.define('quick-checkout-component', QuickCheckoutComponent);
}
